import React from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Msg #987 — Admin: Manage Mods panel. Lists all currently-promoted moderators
// with a one-click demote per row, plus an inline promote form.
export default function ManageModsModal({ open, onClose, requester }) {
  const [mods, setMods] = React.useState([]);
  const [promoteName, setPromoteName] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [note, setNote] = React.useState('');

  const load = React.useCallback(async () => {
    setErr('');
    try {
      const r = await axios.get(`${API}/admin/moderators/list`, { params: { requester } });
      setMods(r.data?.moderators || []);
    } catch (e) {
      const d = e && e.response && e.response.data && e.response.data.detail;
      setErr(d || 'Failed to load moderators.');
    }
  }, [requester]);

  React.useEffect(() => {
    if (open) { setPromoteName(''); setNote(''); load(); }
  }, [open, load]);

  if (!open) return null;

  const promote = async () => {
    if (!promoteName.trim()) return;
    setBusy(true); setErr(''); setNote('');
    try {
      await axios.post(`${API}/admin/promote`, {
        requester, target: promoteName.trim(), room: '*',
      });
      setNote(`Promoted ${promoteName.trim()} to global moderator.`);
      setPromoteName('');
      await load();
    } catch (e) {
      const d = e && e.response && e.response.data && e.response.data.detail;
      setErr(d || 'Promote failed.');
    } finally { setBusy(false); }
  };

  const demote = async (name) => {
    setBusy(true); setErr(''); setNote('');
    try {
      const r = await axios.post(`${API}/admin/demote`, { requester, target: name, room: '*' });
      setNote(`Demoted ${name} (${r.data?.removed || 0} row${(r.data?.removed || 0) === 1 ? '' : 's'} removed).`);
      await load();
    } catch (e) {
      const d = e && e.response && e.response.data && e.response.data.detail;
      setErr(d || 'Demote failed.');
    } finally { setBusy(false); }
  };

  const fmtTs = (ts) => {
    try { return new Date(ts).toLocaleString(); } catch { return ''; }
  };

  return (
    <div
      data-testid="manage-mods-modal"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1200,
        background: 'rgba(20,40,20,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(96vw, 620px)', maxHeight: '86vh',
          display: 'flex', flexDirection: 'column',
          background: '#d6e8c5', border: '2px solid #2d6b1e',
          fontFamily: 'Arial, Helvetica, sans-serif',
          color: '#1c3f1c',
          boxShadow: '0 8px 26px rgba(0,0,0,0.35)',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px', background: '#1c3a1c', color: '#ecfcd9',
          borderBottom: '1px solid #2d6b1e',
        }}>
          <div style={{ fontWeight: 'bold', letterSpacing: 2, textTransform: 'uppercase', fontSize: 14 }}>
            👥 Manage Moderators
          </div>
          <button
            onClick={onClose}
            data-testid="manage-mods-close"
            style={{ background: 'transparent', color: '#ecfcd9',
                     border: '1px solid #ecfcd9', padding: '2px 10px',
                     fontWeight: 'bold', cursor: 'pointer' }}
          >✕</button>
        </div>

        {/* Promote row */}
        <div style={{ padding: '10px 14px', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap',
                      borderBottom: '1px dashed #5b8c47' }}>
          <label style={{ fontSize: 12, fontWeight: 'bold' }}>Promote:</label>
          <input
            data-testid="manage-mods-promote-input"
            value={promoteName}
            onChange={(e) => setPromoteName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && promote()}
            placeholder="username to promote (global)"
            style={{ padding: '3px 8px', border: '1px solid #5b8c47',
                     fontFamily: 'inherit', fontSize: 12, minWidth: 220 }}
          />
          <button
            data-testid="manage-mods-promote-btn"
            onClick={promote}
            disabled={busy || !promoteName.trim()}
            style={{
              background: '#2d6b1e', color: '#ecfcd9',
              border: '1px solid #1c3a1c', padding: '3px 12px',
              fontSize: 11, fontWeight: 'bold', letterSpacing: 1,
              cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1,
            }}
          >PROMOTE</button>
          <span style={{ fontSize: 11, opacity: 0.85, marginLeft: 'auto' }}>
            {mods.length} {mods.length === 1 ? 'moderator' : 'moderators'}
          </span>
        </div>

        {(err || note) && (
          <div style={{ padding: '4px 14px', fontSize: 12 }}>
            {err && <span data-testid="manage-mods-error" style={{ color: '#7a1f1f' }}>{err}</span>}
            {note && <span data-testid="manage-mods-note" style={{ color: '#1c5a1c' }}>{note}</span>}
          </div>
        )}

        {/* Table */}
        <div
          data-testid="manage-mods-list"
          style={{
            flex: 1, minHeight: 120, overflowY: 'auto',
            background: '#f5faee', padding: '4px 0',
          }}
        >
          {mods.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0', opacity: 0.7, fontSize: 12 }}>
              No moderators currently promoted.
            </div>
          ) : mods.map((m) => (
            <div
              key={m.name_lc}
              data-testid={`manage-mods-row-${m.name_lc}`}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '6px 14px',
                borderBottom: '1px dashed #b4d497',
                fontSize: 12,
              }}
            >
              <span style={{ minWidth: 20 }}>⚖️</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 'bold', color: '#1c3a1c' }}>{m.name}</div>
                <div style={{ fontSize: 10, opacity: 0.7 }}>
                  Room scope: <b>{m.room === '*' ? 'GLOBAL' : m.room}</b>
                  {m.promoted_by && <> · by {m.promoted_by}</>}
                  {m.ts && <> · {fmtTs(m.ts)}</>}
                </div>
              </div>
              <button
                data-testid={`manage-mods-demote-${m.name_lc}`}
                onClick={() => demote(m.name)}
                disabled={busy}
                style={{
                  background: '#7a1f1f', color: '#fce2d0',
                  border: '1px solid #3a0e0e', padding: '2px 10px',
                  fontSize: 10, fontWeight: 'bold', letterSpacing: 1,
                  cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1,
                }}
              >DEMOTE</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
